/**
* Utility: Push a GA4 event to the Google Tag Manager dataLayer
* @param {Object} obj A GA4 ecommerce event object to be pushed to the dataLayer
* @see https://developers.google.com/tag-platform/tag-manager/web/datalayer
*/
const pushDataLayerEvent = (obj) => {
  // Fallback for dataLayer
  window.dataLayer = window.dataLayer || [];

  // Clear the previous ecommerce object.
  window.dataLayer.push({ecommerce: null});

  // Push new ecommerce dataLayer event
  window.dataLayer.push(obj);
};

/**
* Calculate Pricing & Discount for Analytics
* @param {Object} arguments
* @property {Object} product The Shopify product object
* @property {Array} product_tags The tags array for this Shopify product, the variant level product data doesn't contain the tags, so you can optionally pass them here.
* @returns {Object} Returns an object with price (full price) and discount properties that can be passed to analytics.
*/
const calculatePrice = ({product, product_tags}) => {
  const pricingData = {};
  const compare_at_price = product.compare_at_price !== null ? Number(product.compare_at_price) : 0;
  const price = Number(product.price);
  const on_sale = compare_at_price !== null && compare_at_price > price;
  let regular_price = on_sale ? compare_at_price : price;
  let discount = on_sale ? compare_at_price - price : 0;

  const tags = Array.isArray(product_tags)
    ? product_tags
    : Array.isArray(product.tags)
      ? product.tags
      : [];

  // Return Value
  pricingData.price = Number(
    new Intl.NumberFormat(window.Shopify.locale, {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Number(regular_price) / 100)
  );
  pricingData.discount = Number(
    new Intl.NumberFormat(window.Shopify.locale, {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Number(discount) / 100)
  );
  return pricingData;
};

/**
* Utility: Calculate the value of an items array to determine the total value of an array of items
* @param {Array} items An array of item objects to reduce to a single monentary value.
*/
const calculateValue = items => items.reduce((value, item) => value += (item.price - item.discount) * item.quantity, 0);

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if(summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function() {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function(event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  setTimeout(() => {
    elementToFocus.focus();
  })
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch(e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = ['ARROWUP', 'ARROWDOWN', 'ARROWLEFT', 'ARROWRIGHT', 'TAB', 'ENTER', 'SPACE', 'ESCAPE', 'HOME', 'END', 'PAGEUP', 'PAGEDOWN']
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if(navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener('focus', () => {
    if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

    if (mouseClick) return;

    currentFocusedElement = document.activeElement;
    currentFocusedElement.classList.add('focused');

  }, true);
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true })

    this.querySelectorAll('button').forEach(
      (button) => button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
  };
}

/*
 * Shopify Common JS
 *
 */
if ((typeof window.Shopify) == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function(fn, scope) {
  return function() {
    return fn.apply(scope, arguments);
  }
};

Shopify.setSelectorByValue = function(selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function(target, eventName, callback) {
  target.addEventListener ? target.addEventListener(eventName, callback, false) : target.attachEvent('on'+eventName, callback);
};

Shopify.postLink = function(path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for(var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function(country_domid, province_domid, options) {
  this.countryEl         = document.getElementById(country_domid);
  this.provinceEl        = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler,this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function() {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function() {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function(e) {
    var opt       = this.countryEl.options[this.countryEl.selectedIndex];
    var raw       = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function(selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function(selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  }
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');
    this.submenuCloseBtn = this.querySelector('#close-submenu');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
    this.querySelectorAll('button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
    if(this.submenuCloseBtn) {
      this.submenuCloseBtn.addEventListener('click', this.onSubmenuCloseButtonClick.bind(this));
    }
  }

  onKeyUp(event) {
    if(event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if(!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary')) : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if(isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches ? addTrapFocus() : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);

        if (summaryElement.classList.contains('header-icon-search') === false && this.submenuCloseBtn) {
          this.submenuCloseBtn.classList.add('visible');
        }
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach(details => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach(submenu => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (this.submenuCloseBtn) {
      this.submenuCloseBtn.classList.remove('visible');
    }
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  onSubmenuCloseButtonClick(event) {
    const detailsElement = event.currentTarget.nextElementSibling.querySelector('.submenu-open').querySelector('details[open]');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');

    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);

    if (this.submenuCloseBtn) {
      this.submenuCloseBtn.classList.remove('visible');
    }
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    }

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.getElementById('shopify-section-header');
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden`);
  }

  closeMenuDrawer(event, elementToFocus) {
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
  }
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      'click',
      this.hide.bind(this, false)
    );
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model, slider-component')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter-current');
    this.pageTotalElement = this.querySelector('.slider-counter-total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.dots = this.querySelectorAll('[id^="slider-dot-"]')

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver(entries => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));

    this.detectiOS();
  }

  detectiOS() {
    // Detect if the user's device is an iOS device, this is to disable custom scrollbars, and iOS 14 and above doesn't support them.
    let isIOS = /iPad|iPhone|iPod/.test(navigator.platform) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    // If the user's device is iOS, add the ios class to the overflow scroller.
    if(isIOS) {
      this.classList.add('is-ios');
    }
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor((this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset);
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();

    // Add click event listeners to each dot element
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.onDotClick(index));
    });
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(new CustomEvent('slideChanged', { detail: {
        currentPage: this.currentPage,
        currentElement: this.sliderItemsToShow[this.currentPage - 1]
      }}));
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }

    // Update the active state of the dots
    this.dots.forEach((dot, index) => {
      dot.classList.toggle('slider-counter-link-active', index === this.currentPage - 1);
    });
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (element.offsetLeft + element.clientWidth) <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition = event.currentTarget.name === 'next' ? this.slider.scrollLeft + (step * this.sliderItemOffset) : this.slider.scrollLeft - (step * this.sliderItemOffset);
    this.slider.scrollTo({
      left: this.slideScrollPosition
    });
  }

  onDotClick(index) {
    this.slideScrollPosition = index * this.sliderItemOffset;
    this.slider.scrollTo({
      left: this.slideScrollPosition,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);

    // Get product, variants, and options data
    this.getProductData();
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(false, '', false);
    this.removeErrorMessage();
    this.updateVariantLabel();

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
    }
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateMasterId() {
    this.currentVariant = this.variantData.find((variant) => {
      return !variant.options.map((option, index) => {
        return this.options[index] === option;
      }).includes(false);
    });
  }

  updateMedia() {
    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;

    const mediaGallery = document.getElementById(`MediaGallery-${this.dataset.section}`);
    mediaGallery.setActiveMedia(`${this.dataset.section}-${this.currentVariant.featured_media.id}`, true);

    const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .slider`);
    if (!modalContent) return;
    const newMediaModal = modalContent.querySelector( `[data-media-id="${this.currentVariant.featured_media.id}"]`).parentNode;
    modalContent.prepend(newMediaModal);
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({ }, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updateVariantLabel() {
    const selectedSwatchLabel = this.querySelectorAll('.product-form-selected-swatch')

    selectedSwatchLabel.forEach((item) => {
      let optionPosition = item.getAttribute('data-position') - 1;
      let variantName = this.currentVariant.options[optionPosition];

      item.innerHTML = variantName;
    })
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm) productForm.handleErrorMessage();
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span');
    if (!addButton) return;

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text) addButtonText.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      addButtonText.textContent = window.variantStrings.addToCart;
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const button = document.getElementById(`product-form-${this.dataset.section}`);
    const addButton = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('[name="add"] > span');
    const price = document.getElementById(`price-${this.dataset.section}`);
    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add('visibility-hidden');
  }

  /**
   * Pulls product level data for this product from a script tag that must be nested inside of the web component.
   * @returns {Object} Shopifys product object for this product
   * @see https://shopify.dev/api/liquid/objects/product
   */
   getProductData() {
    const jsonProductData = this.querySelector('#json-product[type="application/json"]');
    if(!this.productData && jsonProductData !== null) {
      const productData = JSON.parse(jsonProductData.textContent);
      const {product, currency} = productData;
      this.productData = product;
      this.variantData = product.variants;
      this.productOptions = product.options;
      this.currencyCode = currency;
      // Remove the <script> tag we used to load the JSON from the DOM
      jsonProductData.parentNode.removeChild(jsonProductData);
    }
    return Array.isArray(this.productData) ? this.productData : [];
  }
}

customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
  constructor() {
    super();

    // Status Classes
    this.statusClasses = {
      unavailable: 'unavailable',
      outOfStock: 'out-of-stock',
    }

    // Swatches
    this.swatches = /** @type {HTMLInputElement[]} */ (Array.from(this.querySelectorAll('input[type="radio"]')));

    // Prioritize color as the initial swatch that is "clicked" onLoad, or fallback to the first option type.
    const colorIndex = this.productOptions.findIndex(option => option.toLowerCase() === 'color') + 1;
    const priorityOptionIndex = colorIndex ? colorIndex : 1;

    // Set initial swatches state when the page loads
    this.intialSwatchState(priorityOptionIndex);
  }

  /**
   * Pull the values from currently selected swatches (product options) in <variant-radios>
   */
  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
  }

  /**
   * Event Handler: Change
   * @param {Object} event The change event interface emitted from to the radio button's event listener.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/change_event
   */
  onVariantChange(event) {
    // Update currently selected product options that the methods of this component reference internally
    this.updateOptions();

    // Update the currently selected variant id that the methods of this component reference internally
    this.updateMasterId();

    // Update the browsers URL to the newly selected variant
    this.updateURL();

    // AJAX request to refresh the .product-details-wrapper using the section rendering API
    this.refreshProductDetails();

    // Update <product-form> with the correct values for the selected variant, ensuring "Add to Cart" adds the correct variant/options
    this.updateVariantInput();

    // Update the <media-gallery> web component
    this.updateMedia();

    // Update the swatch-option-label to display the name of the selected swatch in a group of swatches
    this.updateVariantLabel();

    // Update the state of the swatch images to show availability
    this.updateSwatchState(event);

    // Emit the newly selected variant to other web components on the PDP page
    this.emitVariantChange({
      ...this.currentVariant,
      statusClasses: this.statusClasses
    });
  }

   /**
   * Return Current Variant
   */
  getCurrentVariant() {
    return this.currentVariant;
  }

  /**
   * Emits the variantChange event
   *
   * When a user changes the variant by choosing a swatch, this event fires to trigger updates in other web components on the page.
   * Other web components have event listeners set-up that listen for this variantChange event, so each web component is responsible for updating itself.
   * @param {Object} detail The detail object can contain additional data that other web components can use.
   * @event variantChange
   * @see https://developer.mozilla.org/en-US/docs/Web/API/CustomEvent/detail
   */
  emitVariantChange(detail) {
    this.dispatchEvent(new CustomEvent('variantChange', {detail}));
  }

  /**
   * Update the state of all swatches after a variant change
   *
   * This function is responsible for indicating if a swatch is unavailable or out of stock. The swatches
   * are radio buttons in the DOM, modified to appear as selectable swatches.
   *
   * @param {Object} event The change event interface emitted from to the radio button's event listener.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/change_event
   */
  updateSwatchState(event) {
    // Reset swatch state
    this.resetSwatchesState();

    // Which fieldset of radio buttons was changed?
    // Corresponds with the indexes in the this.options array below.
    const clickedOptionIndex = event.target.dataset.optionIndex;
    const clickedOptionValue = event.target.value;

    // Values of the on elements for all options that are now :checked, after the change event has fired.
    const currentlySelectedOptions = this.options;

    // The current options selected, but with the clicked option removed. For example, if the user clicked "Color", this will include the currently selected values for everything except color.
    const otherSelectedOptions = currentlySelectedOptions.filter(option => option === clickedOptionValue);

    // If a swatch is not available for all variants, add the outOfStock class to that swatch.
    this.swatches.forEach(swatch => {
        const swatchInStock = this.variantData.filter(variant => variant.options.includes(swatch.value) && variant.available);
        if(!swatchInStock.length) {
          swatch.classList.add(this.statusClasses.outOfStock);
        }
    });

    // Availability of all swatches outside of clicked swatch group
    this.swatches
    .filter(swatch => swatch.dataset.optionIndex !== clickedOptionIndex)
    .forEach(swatch => {
      otherSelectedOptions.forEach(option => {
        const optionVariants = this.variantData.filter(variant => variant.options.includes(option) && variant.options.includes(swatch.value) && variant.available);
        if(!optionVariants.length) {
          swatch.classList.add(this.statusClasses.outOfStock);
        }
      });
    });
  }

  /**
   * Re-renders the product details section when the variant changes on the PDP page
   * @see https://shopify.dev/api/section-rendering
   */
  refreshProductDetails() {
    const productDetailsSection = document.querySelector('.product-details-wrapper');
    const preOrderLink = document.querySelector('.product-preorder-link');
    const qtySelector = document.querySelector('.product-form-quantity');
    if (productDetailsSection !== null && typeof productDetailsSection.id === 'string' && this.currentVariant !== undefined && typeof this.currentVariant.id === 'number') {
      fetch(`${window.location.pathname}?variant=${this.currentVariant.id}&section_id=${productDetailsSection.id}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = document.getElementById(productDetailsSection.id);
        const source = html.getElementById(productDetailsSection.id);
        if (source && destination) destination.innerHTML = source.innerHTML;
        const newPreorderLink = html.querySelector('.product-preorder-link');
        if (newPreorderLink && preOrderLink) preOrderLink.innerHTML = newPreorderLink.innerHTML;
      });
    }
    // Check if preOrderLink exists before accessing its classList
    if (preOrderLink !== null) {
      if (this.currentVariant.available === false) {
        preOrderLink.classList.remove('hidden');
      } else {
        preOrderLink.classList.add('hidden');
      }
    }

    // Check if current variant is out of stock and add/hide OOS subtitle text
    if (this.currentVariant.available === false) {
      qtySelector.classList.add('hidden');

      if(!document.querySelector('.oos-text')) {
        const OOStext = document.createElement("p");
        const productBuyButtons = document.querySelector('.product-buy-buttons');
        const buyButtonsParent = productBuyButtons.parentNode;

        OOStext.classList.add('subtitle-sm', 'oos-text');
        OOStext.innerHTML = 'Temporarily out of stock';
        buyButtonsParent.insertBefore(OOStext, productBuyButtons);
      }
    } else if(document.querySelector('.oos-text')) {
      qtySelector.classList.remove('hidden');

      if(document.querySelector('.oos-text'))  {
        document.querySelector('.oos-text').remove();
      }
    }
  }

  /**
   * Initial swatch state
   *
   * When this web component is constructed, set the initial availability state based on the currently selected options.
   *
   * This method fires a "mock" event as though the user clicked on a swatch, it will pull the data for the :checked swatch
   * for the provided optionIndex, and fire the change event as if that swatch were clicked to update the availability of
   * the other swatches on page load.
   *
   * @param {Number} optionIndex The option position/index for the fieldset of swatches you want to "mock" the change event for.
   * @see https://shopify.dev/api/liquid/objects/product_option#product_option-position
   */
  intialSwatchState(optionIndex) {
    // Intial data set-up
    this.updateOptions();
    this.updateMasterId();
    // Fire mock change event to trigger standard variantChange logic
    const mockEvent = {
      target: {
        dataset: {
          optionIndex: optionIndex.toString()
        },
        value: this.options[optionIndex - 1].toString()
      }
    };
    this.updateSwatchState(mockEvent);
  }

  /**
   * Removes all status classes from every swatch, such as out of stock or unavailable classes.
   */
  resetSwatchesState() {
    this.swatches.forEach(swatch => {
      Object.values(this.statusClasses).forEach(status => swatch.classList.remove(status));
    })
  }
}

customElements.define('variant-radios', VariantRadios);

/**
 * PDP Price
 *
 * The <pdp-price> web component is responsible for rendering prices on the PDP page. More specifically,
 * it has event listeners automatically binded to it in order to respond to the 'variantChange' event that
 * fires when the <variant-radios> component changes the currently selected variant on the PDP page.
 *
 * @extends HTMLElement
 */
class PDPPrice extends HTMLElement {
  constructor() {
    super();
    // Set-up event listener to listen for change event in <variant-radios>
    const variantRadios = document.getElementById(`VariantRadios-${this.dataset.sectionId}`)
    if(variantRadios) {
      variantRadios.addEventListener('variantChange', this.onVariantChange.bind(this))
    }
    this.getCurrencyData();
    this.salePrice = this.querySelectorAll('.price-item.price-item-sale');
    this.regularPrice = this.querySelectorAll('.price-item.price-item-regular');
   }

  /**
   * variantChange event handler
   * @param {Object} e The variantChange custom event interface emitted from the <variant-radios> web component.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/CustomEvent
   */
  onVariantChange(e) {
    const {compare_at_price, price} = e.detail;
    const on_sale = compare_at_price !== null && compare_at_price > price;
    // Update all sale price elements
    this.salePrice.forEach(salePrice => {
      salePrice.innerText = this.formatPrice(price);
    });
    // Update all regular price elements
    this.regularPrice.forEach(regularPrice => {
      regularPrice.innerText = this.formatPrice(on_sale ? compare_at_price : price);
    });
    // Set price class on <pdp-price> element to display on sale pricing or regular pricing
    this.setPriceClasses(e.detail)
  }

  /**
   * Adds the correct classes to the price wrapper based on whether or not the product is on sale.
   * This will show/hide the strikethrough price based on which class gets set.
   * @param {Object} variant The shopify variant object passed in the detail property in the 'variantChange' event.
   * @see https://shopify.dev/api/liquid/objects/variant
   */
  setPriceClasses(variant) {
    // Sold Out
    if(variant.available === false) {
      this.classList.add('price-sold-out');
    } else {
      this.classList.remove('price-sold-out');
    }
    // On Sale
    const on_sale = variant.compare_at_price !== null && variant.compare_at_price > variant.price;
    if(on_sale) {
      this.classList.add('price-on-sale');
    } else {
      this.classList.remove('price-on-sale');
    }
  }

  /**
   * Get currency and money format data
   * @returns {Object} The JSON passed in the #json-currency-data script tag inside of the <pdp-price> web component.
   */
  getCurrencyData() {
    this.currencyData = this.currencyData || JSON.parse(this.querySelector('#json-currency-data[type="application/json"]').textContent);
    return this.currencyData;
  }

  /**
   * Format a price string
   * @param {Number|String} price Should be product.price or product.compare_at_price
   * @see https://shopify.dev/api/liquid/objects/product#product-compare_at_price
   * @see https://shopify.dev/api/liquid/objects/product#product-price
   * @see https://shopify.dev/api/liquid/filters/money
   * @see https://shopify.dev/api/liquid/filters/money_with_currency
   * @returns {String} A formatted price string that has shopifys money format filters applied to it.
   */
  formatPrice(price) {
    const {currency_code_enabled, money_format, money_with_currency_format} = this.currencyData;
    const options = {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    };
    const formatted_price = new Intl.NumberFormat(window.Shopify.locale, options).format(Number(price) / 100);
    const shopify_format = currency_code_enabled ? money_with_currency_format : money_format;
    return shopify_format.replace('{{amount_no_decimals}}', formatted_price);
  }
}

customElements.define('pdp-price', PDPPrice);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}